import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { combineLatest, distinctUntilChanged, first, map, Observable, of, Subscription } from 'rxjs';
import { ChartConfiguration } from 'src/app/pump-simulation/chartConfiguration';
import { createConfiguration, plotData } from 'src/app/shared/util';
import { ShareDataService } from '../../share-data.service';
import { CalculateCoefficientState } from '../../store/calculate-coefficient/calculate-coefficient.reducer';
import { selectCalculateCoefficient } from '../../store/calculate-coefficient/calculate-coefficient.selector';
import { IntermediateState } from '../../store/charts/intermediate-chart/intermediate-chart.reducer';
import { selectIntermediate } from '../../store/charts/intermediate-chart/intermediate-chart.selector';

declare const google: any;

const CI_CHART_ID = 'ciChart';

@Component({
    selector: 'app-ci-chart',
    templateUrl: './ci-chart.component.html',
    styleUrls: ['./ci-chart.component.scss'],
    standalone: false
})
export class CiChartComponent implements OnInit, OnDestroy {

  private subscriptions: Subscription[] = [];
  xAxisToYaxisChart = null;
  showPlaceholder$: Observable<Boolean>;
  @ViewChild(CI_CHART_ID, { static: true }) chartElement: ElementRef;

  constructor(
    private store: Store,
    private shareService: ShareDataService,
  ) { }

  ngOnInit() {
    const chartLoading = this.shareService.getChartItem().subscribe(data => {
      if (data.includes('mci') || data.includes('nci')) {
        this.showPlaceholder$ = of(true);
      }
    });
    this.subscriptions.push(chartLoading);

    const ciChart = this.store.pipe(
      select(selectIntermediate),
      map((data: IntermediateState) => data.intermediateData),
      distinctUntilChanged()
    ).subscribe(data => {
      if (data) {
        this.handleCIGraph();
      }
    });
    this.subscriptions.push(ciChart);
  }

  handleCIGraph() {
    const data = this.store.pipe(
      select(selectIntermediate),
      map((data: IntermediateState) => data.chartItems),
      first()
    ).subscribe(data => {
      if (data.includes('ciChart')) {
        this.showPlaceholder$ = of(true);
        this.createCIGraph();
      }
    });
    this.subscriptions.push(data);
  }

  createCIGraph() {

    const ciChartConfiguration = createConfiguration({ yAxis: 'CI [A]' });

    const ciGraph = combineLatest([
      this.store.pipe(
        select(selectCalculateCoefficient),
        map((data: CalculateCoefficientState) => data)
      ),
      this.store.pipe(
        select(selectIntermediate),
        map((data: IntermediateState) => data),
      ),
    ]).pipe(first()).subscribe(([measuredData, calculatedData]) => {

      const ciMeasuredData = new Map<number, number>();
      const ciCalculatedData = new Map<number, number>();
      const ciMeasuredDataMap = new Map();
      const ciCalculatedDataMap = new Map();

      for (const entry of measuredData.allCoefficientsData.intermediateCoefficents.entries()) {
        ciMeasuredData.set(entry[0], entry[1].ci);
        ciMeasuredDataMap.set(1, ciMeasuredData);
      }

      if (calculatedData.intermediateData) {

        for (const entry of calculatedData.intermediateData.entries()) {
          ciCalculatedData.set(entry[0], entry[1].ci);
          ciCalculatedDataMap.set(-1, ciCalculatedData);
        }
        this.showPlaceholder$ = of(false);
        google.charts.load('current', { packages: ['corechart'] });
        google.charts.setOnLoadCallback(this.drawChart(ciChartConfiguration, this.chartElement.nativeElement, ciCalculatedDataMap, ciMeasuredDataMap));
      }
    });
    this.subscriptions.push(ciGraph);
  }

  drawChart(
    chartConfiguration: ChartConfiguration,
    chart: ElementRef,
    calculation: Map<number, Map<number, number>>,
    measurements: Map<number, Map<number, number>>
  ) {
    return () => {
      let columnIndex = 1;
      let rowIndex = 0;
      const colors = chartConfiguration.colours;
      const data = new google.visualization.DataTable();
      data.addColumn('number', chartConfiguration.xAxisTitle);
      const options = chartConfiguration.options;

      let head;
      ({ head, rowIndex, columnIndex } = plotData(
        calculation, data, rowIndex, chartConfiguration,
        columnIndex, options, colors, 'calculated'));
      ({ head, rowIndex, columnIndex } = plotData(
        measurements, data, rowIndex, chartConfiguration,
        columnIndex, options, colors, 'measured'));

      this.xAxisToYaxisChart = new google.visualization.LineChart(chart);
      google.visualization.events.addListener(this.xAxisToYaxisChart, 'ready', () => {
        // remove loading
      });
      this.xAxisToYaxisChart.draw(data, options);
    };
  }

  ngOnDestroy() {
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
    if(this.xAxisToYaxisChart) {
      this.xAxisToYaxisChart.clearChart();
    }
    // this.store.dispatch(setIntermediateData({ data: null }));
  }
}
